/**
 * Pending Message Types for Optimistic Updates
 *
 * These types support the optimistic message sending system where messages
 * appear immediately in the UI and are synced to the server in the background.
 */

import type { QuotedMessage } from './index';
import type { Attachment } from './dm';
import type { MentionData } from './mentions';

/**
 * Status of a pending message in the optimistic update queue
 */
export type MessageStatus = 'pending' | 'uploading' | 'sending' | 'sent' | 'failed';

/**
 * Chat type discriminator for routing messages
 */
export type ChatType = 'community' | 'dm' | 'leaders';

/**
 * A pending attachment that may still need to be uploaded
 */
export interface PendingAttachment {
  /** Unique identifier for this attachment */
  id: string;
  /** Original file object (only available before page refresh) */
  file?: File;
  /** Upload progress 0-100 */
  uploadProgress: number;
  /** Uploaded attachment data (set once upload completes) */
  uploaded?: Attachment;
  /** Error message if upload failed */
  error?: string;
}

/**
 * A message in the optimistic update queue
 */
export interface PendingMessage {
  /** UUID generated client-side for tracking */
  clientId: string;
  /** Firestore document ID once confirmed by server */
  serverId?: string;
  /** Current status in the send pipeline */
  status: MessageStatus;
  /** Number of retry attempts made */
  retryCount: number;
  /** Error message if failed */
  error?: string;
  /** Overall upload progress for attachments (0-100) */
  uploadProgress?: number;
  /** Timestamp when message was created (for ordering) */
  createdAt: number;
  /** Timestamp when status last changed (for stuck-message detection) */
  statusChangedAt?: number;

  // Message content
  /** Message text */
  text: string;
  /** Pending attachments (files being uploaded) */
  pendingAttachments?: PendingAttachment[];
  /** Already-uploaded attachments (for DMs where uploads are done before sending) */
  uploadedAttachments?: import('./dm').Attachment[];
  /** Quote/reply data */
  quotedMessageId?: string;
  quotedMessage?: QuotedMessage;

  // Context
  /** Whether this is a community chat or DM */
  chatType: ChatType;
  /** Conversation ID (required for DMs) */
  conversationId?: string;

  // Mention data
  /** Mention metadata (for the encrypted envelope) */
  mentions?: MentionData[];
  /** Mentioned user IDs (for the plaintext Firestore field) */
  mentionedUserIds?: string[];

  // User context (needed for display while pending)
  /** User ID of the sender */
  userId: string;
  /** Display name of the sender */
  author: string;
  /** User's role (for community chat badge) */
  authorRole?: string;
}

/**
 * Serializable version of PendingMessage for localStorage persistence
 * Note: File objects cannot be serialized, so attachments need special handling
 */
export interface PersistedPendingMessage {
  clientId: string;
  chatType: ChatType;
  conversationId?: string;
  text: string;
  quotedMessageId?: string;
  quotedMessage?: QuotedMessage;
  /** Indicates attachments were pending but files are lost on refresh */
  hasUnsentAttachments: boolean;
  createdAt: number;
  userId: string;
  author: string;
  authorRole?: string;
}

/**
 * Structure stored in localStorage
 */
export interface PersistedPendingMessages {
  messages: PersistedPendingMessage[];
  timestamp: number;
}

/**
 * Input for creating a new optimistic message
 */
export interface OptimisticMessageInput {
  text: string;
  /** Files to upload (for community chat) */
  files?: File[];
  /** Already-uploaded attachments (for DMs where MessageComposer handles uploads) */
  uploadedAttachments?: import('./dm').Attachment[];
  quotedMessageId?: string;
  quotedMessage?: QuotedMessage;
  /** Mention metadata (for the encrypted envelope) */
  mentions?: MentionData[];
  /** Mentioned user IDs (for the plaintext Firestore field) */
  mentionedUserIds?: string[];
}

/**
 * Constants for the message queue system
 */
export const MESSAGE_QUEUE_CONFIG = {
  /** localStorage key for persisted messages */
  STORAGE_KEY: 'spirit-of-shabbat-pending-messages',
  /** Maximum retry attempts before marking as failed */
  MAX_RETRIES: 3,
  /** Base delay for exponential backoff (ms) */
  RETRY_BASE_DELAY: 1000,
  /** Time after which persisted messages are considered stale (7 days) */
  STALE_THRESHOLD_MS: 7 * 24 * 60 * 60 * 1000,
  /** Delay before removing successfully sent messages from queue (5 minutes) */
  CLEANUP_DELAY_MS: 5 * 60 * 1000,
} as const;
